import { globalStores } from '@/editor/stores';
import { Mesh, MeshBasicMaterial, PlaneGeometry, type ColorRepresentation } from 'three';
import BaseModel2d from './BaseModel2d';
import { EEntityType } from '@/editor/types';
import { getUniqueId } from '@/editor/utils';
import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { pixelRatio2d } from '@/editor/math/const';
import { Line2, LineGeometry, LineMaterial } from 'three/examples/jsm/Addons.js';

export type TRectangleHelperLineType = 'solid' | 'dashed';

export interface IRectangleHelperParams {
  width: number;
  height: number;
  center: IPoint2d;
  backgroundColor: ColorRepresentation;
  lineType: TRectangleHelperLineType;
  lineColor: ColorRepresentation;
  backgroundOpacity?: number;
  lineOpacity?: number;
  dashSize?: number;
  gapSize?: number;
  zoom?: number;
}

const defaultBackgroundOpacity = 0.25;
const defaultLineOpacity = 1;
const defaultDashedLineDashSize = 0.05;
const defaultDashedLineGapSize = 0.025;

export default class RectangleHelper extends BaseModel2d {
  private _lineMaterial: LineMaterial | null = null;
  private _params: IRectangleHelperParams = {
    width: 0,
    height: 0,
    center: { x: 0, y: 0 },
    backgroundColor: 0x4c9dff,
    lineType: 'solid',
    lineColor: 0x4c9dff,
    backgroundOpacity: defaultBackgroundOpacity,
    lineOpacity: defaultLineOpacity,
    dashSize: defaultDashedLineDashSize,
    gapSize: defaultDashedLineGapSize,
    zoom: 1
  };

  constructor() {
    super();

    this._modelGroup.userData.type = EEntityType.RectangleHelper;
    this._modelGroup.userData.entityId = getUniqueId();

    this.setDisplayOrder(ENTITY_RENDER_ORDER.rectangleHelper);

    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
  }

  update(params: IRectangleHelperParams) {
    this._params = { ...this._params, ...params };
    this.clear();
    this.createRect();
    this.updatePosition();
  }

  updatePosition(center = this._params.center) {
    this._params.center = center;
    this._modelGroup.position.set(this._params.center.x / pixelRatio2d, this._params.center.y / pixelRatio2d, 0);
  }

  private createRect() {
    const width = this._params.width / pixelRatio2d;
    const height = this._params.height / pixelRatio2d;
    const bgPlane = new PlaneGeometry(width, height);
    const bgMesh = new Mesh(
      bgPlane,
      new MeshBasicMaterial({
        color: this._params.backgroundColor,
        opacity: this._params.backgroundOpacity,
        transparent: true,
        depthTest: false,
        depthWrite: false
      })
    );
    const line = new LineGeometry();
    line.setPositions([
      -width / 2,
      -height / 2,
      0,
      width / 2,
      -height / 2,
      0,
      width / 2,
      height / 2,
      0,
      -width / 2,
      height / 2,
      0,
      -width / 2,
      -height / 2,
      0
    ]);

    const lineMaterial = this.createLineMaterial();
    const border = new Line2(line, lineMaterial);
    this._lineMaterial = lineMaterial;

    if (this._params.lineType === 'dashed') {
      border.computeLineDistances();
      this.updateScale(this._params.zoom ?? 1);
    }

    this._modelGroup.add(bgMesh);
    this._modelGroup.add(border);
  }

  private createLineMaterial() {
    const zoom = this.getSafeZoom(this._params.zoom);

    return new LineMaterial({
      color: this._params.lineColor,
      opacity: this._params.lineOpacity,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      ...(this._params.lineType === 'dashed'
        ? {
            dashed: true,
            dashSize: ((this._params.dashSize ?? defaultDashedLineDashSize) / zoom) * 100,
            gapSize: ((this._params.gapSize ?? defaultDashedLineGapSize) / zoom) * 100
          }
        : {})
    });
  }

  private getSafeZoom(zoom = 1) {
    return Math.max(zoom, 1e-6);
  }

  public updateScale(zoom: number) {
    this._params.zoom = zoom;
    if (!this._lineMaterial || this._params.lineType !== 'dashed') return;

    const safeZoom = this.getSafeZoom(zoom);
    this._lineMaterial.dashSize = ((this._params.dashSize ?? defaultDashedLineDashSize) / safeZoom) * 100;
    this._lineMaterial.gapSize = ((this._params.gapSize ?? defaultDashedLineGapSize) / safeZoom) * 100;
  }

  override clear() {
    this._lineMaterial = null;
    super.clear();
  }
}
